import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import _ from 'lodash';
import classNames from 'classnames';

import * as CommonStateSelectors from 'State/selectors';
import { ItemList } from 'PointOfSale/modules/common/component/ItemList';
import { createToast, toastType } from 'Common/ToastHelper';
import { DraggablePortal } from 'PointOfSale/modules/common/component/DraggablePortal';
import { selectFoodItem } from 'PointOfSale/modules/mealTypes/state/actions';
import { ITEMS_PER_ROW } from 'PointOfSale/modules/common/constants';
import * as POSSelectors from 'PointOfSale/selectors';
import POSButton from 'PointOfSale/modules/common/component/Button';

const BUTTON_WIDTH = 1;

const getButtonRowStyles = () => ({
  display: 'flex',
  flexDirection: 'row',
  flex: 1,
});

const getButtonStyles = () => ({
  display: 'flex',
  flex: `${BUTTON_WIDTH} 0 ${100 / ITEMS_PER_ROW}%`,
  maxWidth: `${(100 / ITEMS_PER_ROW) * BUTTON_WIDTH}%`,
});

class FoodItemsList extends Component {
  draggingWithTouch = false;

  componentDidMount() {
    if (this.props.isInModal && this.props.modalId) {
      const modal = document.getElementById(this.props.modalId);
      if (modal) {
        this.modal = modal;
      }
    }
  }

  componentDidUpdate(prevProps) {
    const {
      saveOrderNumError,
      saveOrderNumSuccess,
      isSelectedFoodItemsBucket,
      isOnline,
    } = this.props;

    if (isSelectedFoodItemsBucket) {
      if (!prevProps.saveOrderNumError && saveOrderNumError) {
        createToast(
          toastType.error,
          'Error',
          'Unable to save order preference. Please try again.'
        );
      }
      if (!prevProps.saveOrderNumSuccess && saveOrderNumSuccess) {
        if (isOnline) {
          createToast(
            toastType.success,
            'Success',
            'Your order preference has been saved!',
            true
          );
        } else {
          createToast(
            toastType.info,
            'Attention',
            'Your order preference has been saved for this computer, please save while online to complete save process.',
            true,
            15000
          );
        }
      }
    }
  }

  get items() {
    const {
      foodItems,
      currentSelectedMenu,
      currentSelectedTabMenu,
      isSelectedFoodItemsBucket,
      servedItemsOrder,
    } = this.props;

    /** Dont filter selected items based on tab */
    const items = _(
      _.get(
        foodItems,
        [
          currentSelectedMenu,
          isSelectedFoodItemsBucket ? 'All Food Items' : currentSelectedTabMenu,
        ],
        []
      )
    );

    const filtered = isSelectedFoodItemsBucket
      ? items
          .filter('selected')
          .map(i => ({
            ...i,
            OrderNum: servedItemsOrder[i.ItemId[0]].indexOf(i.ItemId),
          }))
          .sortBy('OrderNum')
          .value()
      : items
          .filter(i => !i.selected)
          .sortBy('ShortDescription')
          .value();

    return _.chunk(filtered, ITEMS_PER_ROW);
  }

  selectFoodItem = item => {
    this.props.selectFoodItem(item);
  };

  renderItems = (row, rowIndex) => {
    return (
      <div key={rowIndex} style={getButtonRowStyles()}>
        <ItemList
          items={row}
          renderItem={(item, index) => {
            return (
              <div key={`${item.ItemId}_${index}`} style={getButtonStyles()}>
                {this.renderItemButton(item, index)}
              </div>
            );
          }}
        />
      </div>
    );
  };

  get itemButtonProps() {
    return {
      fluid: true,
      as: 'div',
      onTouchStart: () => {
        this.draggingWithTouch = true;
      },
      onTouchEnd: () => {
        this.draggingWithTouch = false;
      },
      onTouchCancel: () => {
        this.draggingWithTouch = false;
      },
      selected: this.props.isSelectedFoodItemsBucket,
    };
  }

  renderItemButton = (item, index) => {
    return (
      <POSButton
        key={index}
        onClick={() => {
          this.selectFoodItem(item);
        }}
        label={item.ShortDescription}
        className={classNames({
          draggable: this.props.isSelectedFoodItemsBucket,
        })}
        {...this.itemButtonProps}
      />
    );
  };

  renderDraggableItems = (row, rowIndex) => {
    const { isInModal } = this.props;

    return (
      <Droppable
        key={`droppable-${rowIndex}`}
        droppableId={`droppable-${rowIndex}`}
        direction="horizontal"
      >
        {(provided, snapshot) => (
          <div
            style={getButtonRowStyles()}
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            <ItemList
              items={row}
              renderItem={(item, index) => {
                return (
                  <Draggable
                    key={`${index}_${item.ItemId}`}
                    draggableId={`draggable-${item.ItemId}`}
                    index={index}
                  >
                    {(provided, snapshot) => {
                      const top =
                        this.draggingWithTouch && isInModal && this.modal
                          ? _.subtract(
                              provided.draggableProps.style.top,
                              this.modal.offsetTop
                            )
                          : provided.draggableProps.style.top;

                      const left =
                        this.draggingWithTouch && isInModal && this.modal
                          ? _.subtract(
                              provided.draggableProps.style.left,
                              this.modal.offsetLeft
                            )
                          : provided.draggableProps.style.left;

                      const child = (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            ...getButtonStyles(),
                            ...provided.draggableProps.style,
                            top: top ? top : 0,
                            left: left ? left : 0,
                            zIndex: isInModal ? 10001 : 1000,
                          }}
                        >
                          {this.renderItemButton(item, index)}
                        </div>
                      );

                      if (!snapshot.isDragging) {
                        return child;
                      }

                      /**
                       * NOTE:
                       * Portals do not interact well with our nested scroll containers
                       * If we're dragging via touch, then do not render into a Portal
                       */
                      if (this.draggingWithTouch) {
                        return child;
                      }

                      return (
                        <DraggablePortal selector="app">
                          {child}
                        </DraggablePortal>
                      );
                    }}
                  </Draggable>
                );
              }}
            />
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    );
  };

  render() {
    const { isSelectedFoodItemsBucket } = this.props;

    const renderItem = isSelectedFoodItemsBucket
      ? this.renderDraggableItems
      : this.renderItems;

    return <ItemList items={this.items} renderItem={renderItem} />;
  }
}

FoodItemsList.propTypes = {
  enableScrollButtons: PropTypes.bool,
  isFlat: PropTypes.bool,
  foodItems: PropTypes.object,
  currentSelectedMenu: PropTypes.string,
  currentSelectedTabMenu: PropTypes.string,
  selectFoodItem: PropTypes.func,
  modalId: PropTypes.string,
  isInModal: PropTypes.bool,
};

const mapStateToProps = state => ({
  isOnline:
    CommonStateSelectors.getOnline(state) &&
    CommonStateSelectors.getServerUp(state),
  saveOrderNumFetching: POSSelectors.getSaveOrderNumFetching(state),
  saveOrderNumError: POSSelectors.getSaveOrderNumError(state),
  saveOrderNumSuccess: POSSelectors.getSaveOrderNumSuccess(state),
  foodItems: POSSelectors.filteredAllFoodItems(state),
  servedItemsOrder: POSSelectors.getServedItemsOrder(state),
  currentSelectedMenu: POSSelectors.getCurrentSelectedMenu(state),
  currentSelectedTabMenu: POSSelectors.getCurrentSelectedTabMenu(state),
  activeSession: POSSelectors.getActiveSession(state),
  sessions: POSSelectors.getSessions(state),
});

const mapDispatchToProps = {
  selectFoodItem,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(FoodItemsList)
);



// WEBPACK FOOTER //
// ./src/pages/pointofsale/modules/mealTypes/container/FoodItemsList.js